import * as React from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

function fmtFecha(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString("es-CO");
}

const estadoChip = (estado: string) => {
  const e = String(estado || "").trim();
  if (e === "aprobado") return <Chip size="small" label="Aprobado" color="success" />;
  if (e === "rechazado") return <Chip size="small" label="Rechazado" color="error" />;
  if (e === "pendiente_admin") return <Chip size="small" label="Pendiente administrador" color="warning" />;
  if (e === "ninguno") return <Chip size="small" label="Sin flujo activo" variant="outlined" />;
  if (e) return <Chip size="small" label={e} variant="outlined" />;
  return <Chip size="small" label="—" variant="outlined" />;
};

export type RedisennoTrazabilidadVistaProps = {
  data: Record<string, any>;
  tituloExtra?: string;
};

export default function RedisennoTrazabilidadVista({ data, tituloExtra }: RedisennoTrazabilidadVistaProps) {
  const historial = React.useMemo(() => {
    const raw = Array.isArray(data?.historial) ? data.historial : [];
    return [...raw].sort((a: any, b: any) => {
      const ta = new Date(String(a?.fecha || 0)).getTime();
      const tb = new Date(String(b?.fecha || 0)).getTime();
      return tb - ta;
    });
  }, [data?.historial]);

  const hay =
    String(data?.estado || "").trim() !== "" ||
    historial.length > 0 ||
    data?.solicitado_at ||
    data?.resuelto_at ||
    String(data?.motivo || "").trim() !== "";

  if (!hay) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hay datos de rediseño. El flujo comienza al solicitar rediseño desde esta etapa.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {tituloExtra && (
        <Typography variant="subtitle2" fontWeight={700}>
          {tituloExtra}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Estado del flujo:
        </Typography>
        {estadoChip(String(data?.estado || ""))}
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
          Solicitud y resolución
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">
              Motivo (solicitud)
            </Typography>
            <Typography variant="body2">{String(data?.motivo || "—")}</Typography>
          </Grid>
          {[
            ["Solicitado el", data?.solicitado_at],
            ["Solicitado por", data?.solicitado_por],
            ["Resuelto el", data?.resuelto_at],
            ["Resuelto por", data?.resuelto_por],
            ["Comentario administración", data?.comentario_admin],
          ].map(([label, val]) => {
            const str = val != null && String(val).trim() !== "" ? String(val) : "";
            let mostrar = "—";
            if (str) {
              const parsed = Date.parse(str);
              const pareceFecha =
                label !== "Solicitado por" &&
                label !== "Resuelto por" &&
                label !== "Comentario administración" &&
                !Number.isNaN(parsed) &&
                str.length >= 8;
              mostrar = pareceFecha ? fmtFecha(str) : str;
            }
            return (
              <Grid item xs={12} sm={6} key={String(label)}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
                <Typography variant="body2">{mostrar}</Typography>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Historial de eventos ({historial.length})
        </Typography>
        {historial.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aún no hay eventos en el historial (se registrarán al solicitar y al aprobar/rechazar en «Aprobaciones
            rediseño»).
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={180}>Fecha</TableCell>
                <TableCell>Acción</TableCell>
                <TableCell>Motivo / detalle</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Decisión</TableCell>
                <TableCell>Comentario</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.map((h: any, idx: number) => (
                <TableRow key={`${h?.fecha || idx}-${idx}`}>
                  <TableCell>{fmtFecha(h?.fecha)}</TableCell>
                  <TableCell>{String(h?.accion || "—")}</TableCell>
                  <TableCell>{String(h?.detalle || h?.motivo || "—")}</TableCell>
                  <TableCell>{String(h?.usuario || h?.solicitado_por || "—")}</TableCell>
                  <TableCell>{h?.decision != null ? String(h.decision) : "—"}</TableCell>
                  <TableCell>{String(h?.comentario || "—")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Stack>
  );
}
